import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Plus, X, Save, Mail, Phone, User,
  ChevronRight, MapPin, AlertTriangle, ChevronLeft
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import SiteFilter from '@/components/admin/SiteFilter';

const SITE_LABELS = {
  specialist_dental_london: { label: 'SDL', color: 'bg-[#C8A86B]/15 text-[#96793E]' },
  shihab_romeed: { label: 'SR', color: 'bg-stone-100 text-stone-700' },
  london_dental_academy: { label: 'LDA', color: 'bg-blue-50 text-blue-700' },
};

export default function AdminPatients() {
  const { apiFetch } = useAdmin();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [newPatientOpen, setNewPatientOpen] = useState(false);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (siteFilter !== 'all') params.set('siteKey', siteFilter);
      else params.set('excludeSiteKey', 'london_dental_academy');
      
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '50');
      params.set('page', String(page));

      const res = await apiFetch(`/patients?${params}`);
      const data = await res.json();
      setPatients(data.patients || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, siteFilter, search, page]);

  useEffect(() => {
    setPage(1);
  }, [siteFilter, search]);

  useEffect(() => {
    const timer = setTimeout(loadPatients, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadPatients, search]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Directory</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Patients</h2>
          <p className="text-stone-500 text-sm mt-1">{total} patient{total !== 1 ? 's' : ''} registered.</p>
        </div>
        <Button onClick={() => setNewPatientOpen(true)} className="h-10 bg-stone-900 text-white hover:bg-stone-800 gap-2 shrink-0">
          <Plus className="w-4 h-4" /> New Patient
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-white border-stone-200 h-10"
          />
        </div>
        <SiteFilter value={siteFilter} onChange={setSiteFilter} exclude={['london_dental_academy']} />
      </div>

      {/* Patient List */}
      <Card className="border-stone-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : patients.length === 0 ? (
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-600 font-medium">No patients found</p>
            <p className="text-stone-400 text-sm mt-1">
              {search ? 'Try a different search term' : 'Create your first patient to get started'}
            </p>
          </CardContent>
        ) : (
          <div className="divide-y divide-stone-100">
            {patients.map((patient, i) => {
              const site = SITE_LABELS[patient.siteKey] || { label: patient.siteKey, color: 'bg-stone-100 text-stone-600' };
              const bookingCount = patient._count?.bookings ?? 0;
              const hasNoShowRisk = patient.bookings?.length > 0;
              const hasBalance = patient.outstandingBalance > 0;

              return (
                <motion.button
                  key={patient.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.015 }}
                  onClick={() => navigate(`/admin/patients/${patient.id}`)}
                  className="w-full text-left flex items-center gap-4 p-4 hover:bg-stone-50/50 transition-colors group"
                >
                  {/* Avatar */}
                  <div className="h-11 w-11 rounded-full bg-stone-900 flex items-center justify-center text-white font-semibold text-sm shrink-0 group-hover:bg-stone-800 transition-colors">
                    {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900 text-sm">
                        {patient.firstName} {patient.lastName}
                      </p>
                      {hasNoShowRisk && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">
                          <AlertTriangle className="w-3 h-3" /> No-show risk
                        </span>
                      )}
                      {hasBalance && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          Owes {'\u00A3'}{patient.outstandingBalance}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-500">
                      <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /> {patient.email}</span>
                      {patient.phone && <span className="hidden sm:flex items-center gap-1"><Phone className="w-3 h-3" /> {patient.phone}</span>}
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider', site.color)}>
                      {site.label}
                    </span>
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-stone-500">{bookingCount} booking{bookingCount !== 1 ? 's' : ''}</p>
                      {patient.lastVisitDate && (
                        <p className="text-[10px] text-stone-400">Last: {new Date(patient.lastVisitDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 transition-colors" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-stone-500">
            Page {page} of {totalPages} ({total} patient{total !== 1 ? 's' : ''})
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="h-8 gap-1"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="h-8 gap-1"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* New Patient Slide-over */}
      <AnimatePresence>
        {newPatientOpen && (
          <NewPatientForm
            onClose={() => setNewPatientOpen(false)}
            onSuccess={() => { setNewPatientOpen(false); loadPatients(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── New Patient Form ── */
function NewPatientForm({ onClose, onSuccess }) {
  const { apiFetch, siteAccess } = useAdmin();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    siteKey: siteAccess[0] || 'shihab_romeed',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    notes: '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch('/patients', { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create patient');
      toast.success(`${form.firstName} ${form.lastName} added successfully`);
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white shadow-2xl border-l border-stone-200 z-50 flex flex-col"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between shrink-0 bg-stone-50/50">
          <div>
            <h3 className="text-xl font-playfair font-medium text-stone-900">New Patient</h3>
            <p className="text-sm text-stone-500">Register a new patient</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-50">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form id="new-patient-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-stone-900 border-b pb-1">Site</h4>
              <div className="space-y-1">
                <label className="text-xs font-medium text-stone-700">Booking Site</label>
                <select value={form.siteKey} onChange={e => set('siteKey', e.target.value)} className="w-full h-9 rounded-md border border-stone-200 text-sm px-3">
                  {siteAccess.filter(s => s !== 'london_dental_academy').map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-stone-900 border-b pb-1">Personal Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><label className="text-xs font-medium text-stone-700">First Name *</label><Input required value={form.firstName} onChange={e => set('firstName', e.target.value)} className="h-9" /></div>
                <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Last Name *</label><Input required value={form.lastName} onChange={e => set('lastName', e.target.value)} className="h-9" /></div>
                <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Email *</label><Input required type="email" value={form.email} onChange={e => set('email', e.target.value)} className="h-9" /></div>
                <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Phone *</label><Input required value={form.phone} onChange={e => set('phone', e.target.value)} className="h-9" /></div>
                <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Date of Birth</label><Input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} className="h-9" /></div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-stone-900 border-b pb-1">Notes</h4>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} className="w-full rounded-md border border-stone-200 text-sm px-3 py-2 resize-none" placeholder="Internal notes about this patient..." />
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-stone-200 bg-white flex justify-end gap-3 shrink-0">
          <Button variant="outline" onClick={onClose} className="h-10">Cancel</Button>
          <Button type="submit" form="new-patient-form" disabled={saving || !form.firstName || !form.email} className="h-10 bg-stone-900 text-white gap-2 px-6">
            {saving ? 'Creating...' : <><Save className="w-4 h-4" /> Create Patient</>}
          </Button>
        </div>
      </motion.div>
    </>
  );
}
